<template>
  <TabbedBox :loading="pageDataLoading">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-layer-group"></i> Opening Balance - Glass Setup
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-1">
        <FormGroup :form="toolForm">
          <div class="row">
            <div class="col-12">
              <Spinner v-if="pageDataLoading" />

              <StaticDataTable class="static-table-list" :hover="true" :striped="false" smallRows v-show="!pageDataLoading">
                <template v-slot:header>
                  <tr>
                    <th width="20%">Stock</th>
                    <th>Height</th>
                    <th>Width</th>
                    <th>Block Size</th>
                    <th>Container Type</th>
                    <th>Preferred Supplier</th>
                    <th>Sheets On Hand</th>
                    <th>Stock Usage (sheets / month)<br />
                    Production</th>
                    <th>Stock Usage (sheets / month)<br />
                    Linked</th>
                    <th>Stock Value (MA) (per m^2)</th>
                    <th>Actions</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <template v-for="stock in stockList">
                    <tr :key="stock.id" class="static-datatable-striped">
                      <td>
                        <strong><LinkStock :name="`${stock.code} - ${stock.name}`" :id="stock.id" /></strong>
                      </td>
                      <td class="small">Height</td>
                      <td class="small">Width</td>
                      <td class="small">Block Size</td>
                      <td class="small">Container Type</td>
                      <td class="small">Preferred Supplier</td>
                      <td class="small">Sheets On Hand</td>
                      <td class="small">Production Usage</td>
                      <td class="small">Linked Usage</td>
                      <td class="small">Stock Value</td>
                      <td>
                        <FormButton @click="newGlassSize(stock)" class="btn-xxs btn-default btn-keyboard-highlight">New Size</FormButton>
                      </td>
                    </tr>
                    <template v-for="(glassSize, index) in glassSizeListForStock(stock.id)">
                      <tr :key="`size-${index}-${glassSize.id}`">
                        <td class="font-weight-bold"><i class="fas fa-caret-right mr-1 ml-2"></i> Glass Size {{glassSize.height}}x{{glassSize.width}}</td>
                        <td>
                          <FormInputText v-model="glassSize.height" :ref="`glass-size-${glassSize.id}-height`" :readonly="!glassSize.isNew" />
                        </td>
                        <td>
                          <FormInputText v-model="glassSize.width" :readonly="!glassSize.isNew" />
                        </td>
                        <td>
                          <FormInputText v-model="glassSize.blockSize" :readonly="!glassSize.isNew" />
                        </td>
                        <td>
                          <FormSelect v-model="glassSize.stockContainerId" :options="stockContainerOptions" :readonly="!glassSize.isNew" />
                        </td>
                        <td>
                          <FormSelect v-model="glassSize.preferredSupplierId" :options="supplierOptions" :readonly="!glassSize.isNew" />
                        </td>
                        <td>-</td>
                        <td>
                          <FormInputText v-model="glassSize.stockProductionUsage" v-show="glassSize.isNew" :readonly="!glassSize.isNew" />
                        </td>
                        <td>
                          <FormInputText v-model="glassSize.stockLinkedUsage" v-show="glassSize.isNew" :readonly="!glassSize.isNew" />
                        </td>
                        <td>
                          <FormInputCurrency v-model="glassSize.unitPriceMovingAverage" :readonly="!glassSize.isNew" />
                        </td>
                        <td>
                          <FormButton @click="newGlassLevel(stock, glassSize)" class="btn-xxs btn-default btn-keyboard-highlight"><i class="far fa-plus"></i></FormButton>
                          <FormButton v-if="glassSize.isNew" @click="removeGlassSize(glassSize.id)" class="btn-xxs btn-default btn-keyboard-highlight"><i class="far fa-trash-alt"></i></FormButton>
                        </td>
                      </tr>
                      <template v-for="(glassLevel, levelIndex) in glassLevelListForSize(glassSize.id)">
                        <tr :key="`level-${glassSize.id}-${glassLevel.id}`">
                          <td><i class="fas fa-caret-right  mr-1 ml-4"></i> Glass Level #{{levelIndex+1}}</td>
                          <td>
                            <FormInputText v-model="glassLevel.height" :ref="`glass-level-${glassLevel.id}-height`" :readonly="!glassLevel.isNew" />
                          </td>
                          <td>
                            <FormInputText v-model="glassLevel.width" :readonly="!glassLevel.isNew" />
                          </td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>
                            <FormInputText v-model="glassLevel.qtyOnHand" :readonly="!glassLevel.isNew" />
                          </td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>
                            <FormButton @click="newGlassLevel(stock, glassSize)" class="btn-xxs btn-default btn-keyboard-highlight mr-1"><i class="far fa-plus"></i></FormButton>
                            <FormButton v-if="glassLevel.isNew" @click="removeGlassLevel(glassLevel.id)" class="btn-xxs btn-default btn-keyboard-highlight"><i class="far fa-trash-alt"></i></FormButton>
                          </td>
                        </tr>
                      </template>
                    </template>
                    <tr :key="`spacer-${stock.id}`">
                      <td colspan="11" class="py-4">&nbsp;</td>
                    </tr>
                  </template>
                </template>
              </StaticDataTable>

              <FormHorizontalRule />
              <FormButton class="btn btn-primary btn-sm" type="submit" :loading="submitting" @click.prevent="submitGlassDetails">Update</FormButton>
            </div>
          </div>
        </FormGroup>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form'
import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'AdminToolGlassSetup',
  mixins: [form],
  data() {
    return {
      stockList: [],
      glassSizeList: [],
      glassLevelList: [],
      stockContainerList: [],
      supplierList: [],
      toolForm: {},
      submitting: false
    }
  },
  computed: {
    stockContainerOptions() {
      return _.concat(
        [{ value: null, name: 'None' }],
        this.stockContainerList.map(container => {
          return {
            value: container.id,
            name: `${container.code} - ${container.name}`
          }
        })
      )
    },
    supplierOptions() {
      return _.concat(
        [{ value: null, name: 'None' }],
        this.supplierList.map(supplier => {
          return {
            value: supplier.id,
            name: supplier.name
          }
        })
      )
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.inventoryService.getStockFilteredList('', InventoryStockTypes.glass),
        this.$apiService.inventoryService.getGlassSizeList(),
        this.$apiService.inventoryService.getGlassLevelList(),
        this.$apiService.inventoryService.getStockContainerList(),
        this.$apiService.contactService.getContactList({
          isActive: 1,
          isSupplier: 1,
          isSupplierPricingBulk: 1
        })
      ]))
      .then(results => {
        this.stockList = results[0].data
        this.glassSizeList = results[1].data.map(glassSize => {
          var output = _.cloneDeep(glassSize)
          output.isNew = false
          output.stockProductionUsage = 0
          output.stockLinkedUsage = 0
          return output
        })
        this.glassLevelList = results[2].data.map(glassLevel => {
          var output = _.cloneDeep(glassLevel)
          output.isNew = false
          return output
        })
        this.stockContainerList = results[3].data.filter(stockContainer => stockContainer.isActive)
        this.supplierList = results[4].data
      })
    },
    glassSizeListForStock(stockId) {
      return this.glassSizeList.filter(glassSize => glassSize.stockId == stockId)
    },
    glassLevelListForSize(glassSizeId) {
      return this.glassLevelList.filter(glassLevel => glassLevel.glassSizeId == glassSizeId)
    },

    newGlassSize(stock) {
      let newGlassSize = {
        id: `temp-id-` + uuidv4(),
        isNew: true,
        width: 0,
        height: 0,
        isActive: true,
        blockSize: 0,
        maintainLevel: 0,
        reorderLevel: 0,
        unitPriceMovingAverage: 0,
        stockProductionUsage: 0,
        stockLinkedUsage: 0,
        preferredSupplierId: null,
        stockContainerId: null,
        stockId: stock.id
      }
      this.glassSizeList.push(newGlassSize)

      this.$nextTick(() => {
        let heightEntry = this.$refs[`glass-size-${newGlassSize.id}-height`][0]
        heightEntry.select()
        heightEntry.focus()
        this.scrollToComponent(heightEntry)
      })
    },
    newGlassLevel(stock, glassSize) {
      let newGlassLevel = {
        id: `temp-id-` + uuidv4(),
        isNew: true,
        width: glassSize.width,
        height: glassSize.height,
        isOffcut: false,
        qtyOnHand: 0,
       glassSizeId: glassSize.id,
       stockId: stock.id
      }
      this.glassLevelList.push(newGlassLevel)

      this.$nextTick(() => {
        let heightEntry = this.$refs[`glass-level-${newGlassLevel.id}-height`][0]
        heightEntry.select()
        heightEntry.focus()
        this.scrollToComponent(heightEntry)
      })
    },
    removeGlassSize(glassSizeId) {
      this.glassSizeList = this.glassSizeList.filter(glassSize => glassSize.id != glassSizeId)
      
      // Remove all glass levels associated with the glass size
      this.glassLevelList = this.glassLevelList.filter(glassLevel => glassLevel.glassSizeId != glassSizeId)
    },
    removeGlassLevel(glassLevelId) {
      this.glassLevelList = this.glassLevelList.filter(glassLevel => glassLevel.id != glassLevelId)
    },

    submitGlassDetails() {
      this.submitting = true

      var glassData = {
        glassSizeList: this.convertGlassSizeList()
      }
     
      this.$data.toolForm = this.clearFormResponse()

      this.$apiService.adminService.updateOpeningBalanceGlass(glassData)
      .then(response => {
        this.submitting = false

        this.successToast('Glass Details Updated')
        this.$router.push(`/admin/tools`)
      })
      .catch(err => {
        // Process errors
        this.$data.toolForm = this.processFormResponse(err)
        this.submitting = false
      })
    },

    // Prepare the glass size list for sending to the service
    convertGlassSizeList() {
      return this.glassSizeList.map(glassSize => {
        var output = _.clone(glassSize)

        if(output.isNew)
          delete output.id

        output.glassLevels = this.convertGlassLevelList(glassSize.id)

        return output
      })
    },
    convertGlassLevelList(glassSizeId) {
      return this.glassLevelList
      .filter(glassLevel => glassLevel.glassSizeId == glassSizeId)
      .map(glassLevel => {
        var output = _.clone(glassLevel)

        if(output.isNew)
          delete output.id
        delete output.glassSizeId

        return output
      })
    }
  }
}
</script>